import type { DocumentMeta, Point, Rect, RGBA, SelectionCombineMode } from '../core/types';
import {
  colorRangeCoverage,
  growCoverage,
  magicWandCoverage,
  similarCoverage,
  type ColorSelectImage,
} from '../selection/colorSelect';
import {
  borderCoverage,
  contractCoverage,
  expandCoverage,
  featherCoverage,
  smoothCoverage,
} from '../selection/modify';
import type { Engine } from './engine';
import { selectionOp, type EngineHistoryOp } from './historyOps';

export type SelectionModifyKind = 'border' | 'smooth' | 'expand' | 'contract' | 'feather';

export interface WandOptions {
  tolerance: number;
  contiguous: boolean;
  antialias: boolean;
  sampleAllLayers: boolean;
}

function fullRect(doc: DocumentMeta): Rect {
  return { x: 0, y: 0, width: doc.width, height: doc.height };
}

/**
 * Pixels the colour-based selections look at. Sampling all layers means the
 * flattened image, which is what makes a wand click follow what the user sees.
 */
export function selectionImage(
  engine: Engine,
  doc: DocumentMeta,
  sampleAllLayers: boolean,
): ColorSelectImage | null {
  if (sampleAllLayers) {
    const flat = engine.flatten(doc);
    return { pixels: flat.data, width: flat.width, height: flat.height };
  }
  const layerId = doc.activeLayerId;
  const surface = layerId ? engine.resourcesFor(doc).layer(layerId)?.surface : null;
  if (!surface) return null;
  return { pixels: surface.readPixels(), width: surface.width, height: surface.height };
}

/** Feeds a full-document coverage buffer into the selection through the usual combine path. */
export function applySelectionCoverage(
  engine: Engine,
  doc: DocumentMeta,
  coverage: Uint8Array,
  mode: SelectionCombineMode,
  feather = 0,
): EngineHistoryOp[] {
  const selection = engine.resourcesFor(doc).selection;
  const before = engine.snapshotSelection(doc);
  selection.combineShape(engine.renderer, { rect: fullRect(doc), data: coverage }, mode, feather);
  selection.refresh();
  return [selectionOp(before, engine.snapshotSelection(doc))];
}

/** Replaces the selection with a buffer that was derived from the current one. */
function replaceSelection(
  engine: Engine,
  doc: DocumentMeta,
  next: Uint8Array,
): EngineHistoryOp[] {
  const selection = engine.resourcesFor(doc).selection;
  const before = engine.snapshotSelection(doc);
  selection.restore(next);
  return [selectionOp(before, engine.snapshotSelection(doc))];
}

export function modifySelection(
  engine: Engine,
  doc: DocumentMeta,
  kind: SelectionModifyKind,
  amount: number,
): EngineHistoryOp[] | null {
  const selection = engine.resourcesFor(doc).selection;
  if (selection.isEmpty || amount <= 0) return null;

  const data = selection.snapshot();
  const { width, height } = selection;
  const next = (() => {
    switch (kind) {
      case 'border':
        return borderCoverage(data, width, height, amount);
      case 'smooth':
        return smoothCoverage(data, width, height, amount);
      case 'expand':
        return expandCoverage(data, width, height, amount);
      case 'contract':
        return contractCoverage(data, width, height, amount);
      case 'feather':
        return featherCoverage(data, width, height, amount);
    }
  })();
  return replaceSelection(engine, doc, next);
}

export function wandSelection(
  engine: Engine,
  doc: DocumentMeta,
  seed: Point,
  mode: SelectionCombineMode,
  options: WandOptions,
  feather: number,
): EngineHistoryOp[] | null {
  const image = selectionImage(engine, doc, options.sampleAllLayers);
  if (!image) return null;
  const coverage = magicWandCoverage(image, seed, {
    tolerance: options.tolerance,
    contiguous: options.contiguous,
    antialias: options.antialias,
  });
  return applySelectionCoverage(engine, doc, coverage, mode, feather);
}

export function growSelection(
  engine: Engine,
  doc: DocumentMeta,
  tolerance: number,
  sampleAllLayers: boolean,
): EngineHistoryOp[] | null {
  const selection = engine.resourcesFor(doc).selection;
  const image = selectionImage(engine, doc, sampleAllLayers);
  if (!image || selection.isEmpty) return null;
  return replaceSelection(engine, doc, growCoverage(image, selection.snapshot(), tolerance));
}

export function similarSelection(
  engine: Engine,
  doc: DocumentMeta,
  tolerance: number,
  sampleAllLayers: boolean,
): EngineHistoryOp[] | null {
  const selection = engine.resourcesFor(doc).selection;
  const image = selectionImage(engine, doc, sampleAllLayers);
  if (!image || selection.isEmpty) return null;
  return replaceSelection(engine, doc, similarCoverage(image, selection.snapshot(), tolerance));
}

export function colorRangeSelection(
  engine: Engine,
  doc: DocumentMeta,
  samples: readonly RGBA[],
  fuzziness: number,
  invert: boolean,
  mode: SelectionCombineMode,
): EngineHistoryOp[] | null {
  const image = selectionImage(engine, doc, true);
  if (!image) return null;
  const coverage = colorRangeCoverage(image, samples, { fuzziness, invert });
  return applySelectionCoverage(engine, doc, coverage, mode);
}

/** Preview for the Color Range dialog: the same coverage, as a greyscale image. */
export function colorRangePreview(
  image: ColorSelectImage,
  samples: readonly RGBA[],
  fuzziness: number,
  invert: boolean,
): ImageData {
  const coverage = colorRangeCoverage(image, samples, { fuzziness, invert });
  const rgba = new Uint8ClampedArray(coverage.length * 4);
  for (let i = 0; i < coverage.length; i += 1) {
    const value = coverage[i];
    rgba[i * 4] = value;
    rgba[i * 4 + 1] = value;
    rgba[i * 4 + 2] = value;
    rgba[i * 4 + 3] = 255;
  }
  return new ImageData(rgba, image.width, image.height);
}
